import { useI18n } from '../../../shared/i18n/I18nProvider';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { configureTransport, invalidateScope } from '../../../shared/api/httpClient';
import { ApiError, errorMessage } from '../../../shared/api/apiError';
import { authApi } from '../api/authApi';
import type { AuthData, Store } from '../model/auth.types';
interface SessionContextValue {
  auth: AuthData | null;
  status: 'loading' | 'authenticated' | 'anonymous' | 'error';
  transitioning: boolean;
  bootstrapError: string;
  bootstrap: () => Promise<void>;
  acceptLogin: (data: AuthData) => void;
  logout: () => Promise<void>;
  selectStore: (store: Store) => Promise<void>;
}
const SessionContext = createContext<SessionContextValue | null>(null);
export function SessionProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();

  const [auth, setAuth] = useState<AuthData | null>(null);
  const [status, setStatus] = useState<SessionContextValue['status']>('loading');
  const [transitioning, setTransitioning] = useState(false);
  const [bootstrapError, setBootstrapError] = useState('');
  const ref = useRef<AuthData | null>(null); const epoch = useRef(0);
  const queryClient = useQueryClient();
  const commit = useCallback((data: AuthData | null) => { ref.current = data; setAuth(data); setStatus(data ? 'authenticated' : 'anonymous'); }, []);
  const clear = useCallback(() => { setTransitioning(false); epoch.current++; invalidateScope(); void queryClient.cancelQueries(); queryClient.clear(); commit(null); }, [commit, queryClient]);
  const bootstrap = useCallback(async () => {
    const current = epoch.current;
    setTransitioning(false); setStatus('loading'); setBootstrapError('');
    try { const result = await authApi.session(); if (epoch.current === current) commit(result.authenticated ? result : null); }
    catch (error) {
      if (epoch.current !== current) return;
      if (error instanceof ApiError && error.status === 401) commit(null);
      else { setBootstrapError(errorMessage(error)); setStatus('error'); }
    }
  }, [commit]);
  useEffect(() => { configureTransport({ onExpired: clear, onContextChanged: () => { epoch.current++; invalidateScope(); void queryClient.cancelQueries(); queryClient.clear(); void bootstrap(); } }); void bootstrap(); }, [bootstrap, clear, queryClient]);
  const acceptLogin = useCallback((data: AuthData) => { epoch.current++; invalidateScope(); void queryClient.cancelQueries(); queryClient.clear(); commit(data); }, [commit, queryClient]);
  const logout = useCallback(async () => {
    epoch.current++; invalidateScope(); setTransitioning(true); await queryClient.cancelQueries();
    try { await authApi.logout(); clear(); } finally { setTransitioning(false); }
  }, [clear, queryClient]);
  const selectStore = useCallback(async (store: Store) => {
    const current = ref.current; if (!current) throw new Error(t("Phiên đăng nhập đã kết thúc."));
    const version = ++epoch.current;
    invalidateScope(); setTransitioning(true); await queryClient.cancelQueries(); queryClient.clear();
    try {
      const result = await authApi.selectContext(store.tenant_id, store.id);
      if (epoch.current === version && ref.current === current) { invalidateScope(); queryClient.clear(); commit(result); }
    } finally { if (epoch.current === version) setTransitioning(false); }
  }, [commit, queryClient]);
  return <SessionContext.Provider value={{ auth, status, transitioning, bootstrapError, bootstrap, acceptLogin, logout, selectStore }}>{children}</SessionContext.Provider>;
}
export function useSession() { const value = useContext(SessionContext); if (!value) throw new Error('SessionProvider is required.'); return value; }
